import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import { cn, useAnalytics, useWizard } from '@restworld/utils-common';
import { Button } from '@restworld/ui-ds-base';

import { FormInputProps } from '../worker-application-wizard.types';
import { WizardPauseSaveConfirmModal } from './wizard-step.components';
import { useWorkerApplicationCxt } from '../worker-application-wizard.context';
import { stepsMapping, useAutoApplyJob, useFormInputsHandler } from './form-inputs.hooks';
import { SubmitButton, WizardStepForm } from './form-inputs.components';

export const FormInputs = (props: FormInputProps) => {
  const { canBeSkipped, id, entryTime } = props;

  const { handleSubmit, setValue } = useFormContext();
  const { loading, setOverlayModalContent, setSkippedSteps } = useWorkerApplicationCxt();
  const { activeStep, nextStep, previousStep } = useWizard();
  const { onSubmit } = useFormInputsHandler({ entryTime });
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const recordEvent = useCallback(() => {
    trackEvent('questionnaire_progress', {
      step_number: activeStep,
      step_name: stepsMapping[activeStep as number],
      time_spent: entryTime ? (Date.now() - entryTime) / 1000 : undefined,
      next_action: 'fill_later',
    });
  }, [activeStep, entryTime, trackEvent]);

  const canSkippedButtonEl = useMemo(() => {
    if (activeStep === 11) return;
    if (activeStep === 12 || activeStep === 13)
      return (
        <Button
          variant="tertiary"
          className="text-black"
          onClick={() =>
            setOverlayModalContent({
              visible: true,
              content: <WizardPauseSaveConfirmModal onRecordEvent={recordEvent} />,
            })
          }
        >
          {t('common:save_and_exit')}
        </Button>
      );
    return (
      <Button
        variant="tertiary"
        className="text-black"
        onClick={() => {
          recordEvent();
          setSkippedSteps((p) => (!p.includes(activeStep) ? [...p, activeStep] : [...p]));
          if (id) setValue(id, undefined);
          nextStep();
        }}
      >
        {/* ui-ds-base Button renders children, not the old-ds `title` prop (which rendered an empty button). */}
        {t('common:fill_in_later')}
      </Button>
    );
  }, [activeStep, t, setOverlayModalContent, recordEvent, setSkippedSteps, id, setValue, nextStep]);
  useAutoApplyJob();
  return (
    <Form noValidate autoComplete="off" action="post" onSubmit={handleSubmit(onSubmit)}>
      {canBeSkipped ? <div className="self-center md:self-end">{canSkippedButtonEl}</div> : null}
      {(activeStep === 11 || activeStep === 4) && (
        <div className="hidden md:block">
          {/* Icon-only back button. Master used the old-ds `icon="ArrowLeftIcon"` prop; ui-ds-base
              Button takes `StartIcon`, so without it the button rendered as an empty box. */}
          <Button
            variant="tertiary"
            StartIcon={ArrowLeftIcon}
            aria-label="back"
            onClick={() => {
              previousStep();
              trackEvent('questionnaire_progress', {
                step_number: activeStep,
                step_name: stepsMapping[activeStep as number],
                time_spent: entryTime ? (Date.now() - entryTime) / 1000 : undefined,
                next_action: 'back',
              });
            }}
          />
        </div>
      )}
      <WizardStepForm {...props} />
      {activeStep !== 4 && <SubmitButton loading={loading} entryTime={entryTime} />}
    </Form>
  );
};
export const HelperTextRElement = ({ helperText }: { helperText?: string }) =>
  helperText ? <span className="mt-2 text-sm text-red-600 font-normal">{helperText}</span> : null;
const Form = ({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form className={cn('flex flex-col gap-4 pb-8 md:w-full md:mx-[2rem]', className)} {...props} />
);
